/**
 * Il catalogo dei widget della dashboard: cosa sono, chi li vede, in che
 * taglie si leggono bene, quali impostazioni hanno.
 *
 * Qui non c'e' grafica: queste regole le usano la lettura della disposizione
 * salvata, le dashboard di partenza, la finestra "Aggiungi widget" e i test.
 * I componenti che disegnano ogni widget stanno in
 * src/components/dashboard/widget/registro.ts.
 *
 * I ruoli di ogni widget sono dentro quelli che il database lascia leggere
 * per i suoi dati (vedi RUOLI_FONTE): un widget che poi non riesce a caricare
 * niente non deve nemmeno comparire.
 */

import { USER_ROLES, type UserRole } from '../../types/roles';

/** Piccolo (un quadratino), Medio (due in fila), Grande (due per due), Tutta la riga. */
export type Taglia = 'S' | 'M' | 'L' | 'XL';
export const TAGLIE: readonly Taglia[] = ['S', 'M', 'L', 'XL'];

export type Gruppo = 'oggi' | 'soldi' | 'commerciale' | 'strumenti';

export const GRUPPI: ReadonlyArray<{ chiave: Gruppo; titolo: string }> = [
  { chiave: 'oggi', titolo: 'Da fare oggi' },
  { chiave: 'soldi', titolo: 'Soldi' },
  { chiave: 'commerciale', titolo: 'Commerciale' },
  { chiave: 'strumenti', titolo: 'Mailing e strumenti' },
];

export const TIPI_WIDGET = [
  'impegni-oggi',
  'lead-da-richiamare',
  'bozze-abbonamenti',
  'rinnovi-abbonamenti',
  'fatturato',
  'da-incassare',
  'fatture-scadute',
  'andamento-mensile',
  'ricavi-ricorrenti',
  'nuovi-lead',
  'pipeline-lead',
  'lead-per-fonte',
  'offerte-aperte',
  'ultima-campagna',
  'contatti-mailing',
  'azioni-rapide',
  'nota',
] as const;

export type TipoWidget = (typeof TIPI_WIDGET)[number];

type Vuote = Record<string, never>;

export interface MappaImpostazioni {
  'impegni-oggi': Vuote;
  'lead-da-richiamare': { soloMiei: boolean };
  'bozze-abbonamenti': Vuote;
  'rinnovi-abbonamenti': Vuote;
  fatturato: { periodo: 'mese' | 'trimestre' | 'anno'; confronto: boolean };
  'da-incassare': Vuote;
  'fatture-scadute': Vuote;
  'andamento-mensile': { mesi: 6 | 12 };
  'ricavi-ricorrenti': Vuote;
  'nuovi-lead': { periodo: 'settimana' | 'mese' };
  'pipeline-lead': Vuote;
  'lead-per-fonte': { periodo: 'mese' | 'anno' | 'sempre' };
  'offerte-aperte': { soloMie: boolean };
  'ultima-campagna': Vuote;
  'contatti-mailing': Vuote;
  /** Vuoto = i collegamenti di partenza del ruolo. */
  'azioni-rapide': { collegamenti: string[] };
  nota: { titolo: string; testo: string };
}

/** Da dove prende i dati un widget: serve a controllare i ruoli. */
export type ChiaveFonte =
  | 'calendario'
  | 'lead'
  | 'bozzeAbbonamenti'
  | 'abbonamenti'
  | 'fatture'
  | 'rate'
  | 'costi'
  | 'offerte'
  | 'campagne'
  | 'contatti';

/**
 * Chi puo' leggere ogni fonte, come dicono le regole di accesso del database
 * (migrazioni 00, 03, 10, 11). Il calendario passa da una funzione che
 * accetta ogni utente collegato.
 */
export const RUOLI_FONTE: Record<ChiaveFonte, readonly UserRole[]> = {
  calendario: USER_ROLES,
  lead: ['admin', 'commerciale', 'sales', 'amministrazione'],
  bozzeAbbonamenti: USER_ROLES,
  abbonamenti: ['admin', 'commerciale', 'sales', 'amministrazione'],
  fatture: USER_ROLES,
  // Le singole rate le vedono solo admin e amministrazione (le fatture
  // importate non hanno un autore che possa vederle).
  rate: ['admin', 'amministrazione'],
  costi: ['admin', 'commerciale', 'amministrazione'],
  offerte: ['admin', 'commerciale', 'sales', 'amministrazione'],
  campagne: ['admin', 'commerciale', 'sales', 'amministrazione', 'lettore'],
  contatti: ['admin', 'commerciale', 'sales', 'amministrazione', 'lettore'],
};

export interface DefinizioneWidget<T extends TipoWidget = TipoWidget> {
  tipo: T;
  titolo: string;
  /** Una riga che spiega a cosa serve, per il catalogo. */
  descrizione: string;
  gruppo: Gruppo;
  ruoli: readonly UserRole[];
  fonti: readonly ChiaveFonte[];
  taglie: readonly Taglia[];
  tagliaPredefinita: Taglia;
  /** Quante copie se ne possono mettere. */
  massimo: number;
  /** Il collegamento "Apri" verso la pagina completa. */
  apri?: { etichetta: string; percorso: string; ruoli: readonly UserRole[] };
  impostazioniPredefinite: MappaImpostazioni[T];
  /** Ripulisce le impostazioni lette dal database; non solleva mai errori. */
  leggiImpostazioni: (grezzo: unknown) => MappaImpostazioni[T];
}

// ---------------------------------------------------------------------------
// Lettura prudente delle impostazioni
// ---------------------------------------------------------------------------

const oggetto = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

function sceltaTra<V>(valore: unknown, ammessi: readonly V[], predefinito: V): V {
  return ammessi.includes(valore as V) ? (valore as V) : predefinito;
}

const booleano = (valore: unknown, predefinito: boolean): boolean =>
  typeof valore === 'boolean' ? valore : predefinito;

export const MAX_TITOLO_NOTA = 40;
export const MAX_TESTO_NOTA = 500;

const testoLimitato = (valore: unknown, massimo: number): string =>
  typeof valore === 'string' ? valore.slice(0, massimo) : '';

const nessunaImpostazione = (): Vuote => ({});

// ---------------------------------------------------------------------------
// Il catalogo
// ---------------------------------------------------------------------------

const TUTTI = USER_ROLES;
const SOLDI: readonly UserRole[] = ['admin', 'commerciale', 'amministrazione'];
const INCASSI: readonly UserRole[] = ['admin', 'amministrazione'];
const COMMERCIALE: readonly UserRole[] = ['admin', 'commerciale', 'sales', 'amministrazione'];

export const CATALOGO: { readonly [T in TipoWidget]: DefinizioneWidget<T> } = {
  'impegni-oggi': {
    tipo: 'impegni-oggi',
    titolo: 'Impegni di oggi',
    descrizione: 'Il calendario aziendale di oggi e le prossime azioni sui lead.',
    gruppo: 'oggi',
    ruoli: TUTTI,
    fonti: ['calendario'],
    taglie: ['M', 'L', 'XL'],
    tagliaPredefinita: 'L',
    massimo: 1,
    apri: { etichetta: 'Calendario', percorso: '/calendar', ruoli: COMMERCIALE },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'lead-da-richiamare': {
    tipo: 'lead-da-richiamare',
    titolo: 'Lead da richiamare',
    descrizione: 'Promemoria scaduti, azioni in ritardo e lead fermi da troppi giorni.',
    gruppo: 'oggi',
    ruoli: COMMERCIALE,
    fonti: ['lead'],
    taglie: ['S', 'M', 'L'],
    tagliaPredefinita: 'L',
    massimo: 1,
    apri: { etichetta: 'Lead', percorso: '/leads', ruoli: COMMERCIALE },
    impostazioniPredefinite: { soloMiei: false },
    leggiImpostazioni: (g) => ({ soloMiei: booleano(oggetto(g).soloMiei, false) }),
  },
  'bozze-abbonamenti': {
    tipo: 'bozze-abbonamenti',
    titolo: 'Fatture di abbonamento da emettere',
    descrizione: 'Le bozze preparate stanotte dagli abbonamenti, da controllare ed emettere.',
    gruppo: 'oggi',
    ruoli: SOLDI,
    fonti: ['bozzeAbbonamenti'],
    taglie: ['S', 'M', 'L'],
    tagliaPredefinita: 'M',
    massimo: 1,
    apri: { etichetta: 'Bozze', percorso: '/invoices?status=draft', ruoli: SOLDI },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'rinnovi-abbonamenti': {
    tipo: 'rinnovi-abbonamenti',
    titolo: 'Abbonamenti in scadenza',
    descrizione: "Contratti già scaduti o in scadenza entro 30 giorni.",
    gruppo: 'oggi',
    ruoli: COMMERCIALE,
    fonti: ['abbonamenti'],
    taglie: ['S', 'M', 'L'],
    tagliaPredefinita: 'M',
    massimo: 1,
    apri: { etichetta: 'Abbonamenti', percorso: '/subscriptions', ruoli: COMMERCIALE },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  fatturato: {
    tipo: 'fatturato',
    titolo: 'Fatturato',
    descrizione: "Imponibile emesso nel mese, trimestre o anno, a confronto con l'anno prima.",
    gruppo: 'soldi',
    ruoli: SOLDI,
    fonti: ['fatture'],
    taglie: ['S', 'M'],
    tagliaPredefinita: 'S',
    massimo: 3,
    apri: { etichetta: 'Analisi', percorso: '/analisi-aziendali', ruoli: SOLDI },
    impostazioniPredefinite: { periodo: 'anno', confronto: true },
    leggiImpostazioni: (g) => ({
      periodo: sceltaTra(oggetto(g).periodo, ['mese', 'trimestre', 'anno'] as const, 'anno'),
      confronto: booleano(oggetto(g).confronto, true),
    }),
  },
  'da-incassare': {
    tipo: 'da-incassare',
    titolo: 'Da incassare',
    descrizione: "Quanto devono ancora pagarti i clienti, e quanto è già scaduto.",
    gruppo: 'soldi',
    ruoli: INCASSI,
    fonti: ['fatture', 'rate'],
    taglie: ['S', 'M'],
    tagliaPredefinita: 'S',
    massimo: 1,
    apri: { etichetta: 'Analisi', percorso: '/analisi-aziendali', ruoli: INCASSI },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'fatture-scadute': {
    tipo: 'fatture-scadute',
    titolo: 'Fatture scadute',
    descrizione: "Le fatture non pagate oltre la scadenza, dalla più vecchia.",
    gruppo: 'soldi',
    ruoli: INCASSI,
    fonti: ['fatture', 'rate'],
    taglie: ['M', 'L'],
    tagliaPredefinita: 'L',
    massimo: 1,
    apri: { etichetta: 'Analisi', percorso: '/analisi-aziendali', ruoli: INCASSI },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'andamento-mensile': {
    tipo: 'andamento-mensile',
    titolo: 'Andamento mensile',
    descrizione: 'Fatturato, incassato e costi mese per mese, tutti senza IVA.',
    gruppo: 'soldi',
    ruoli: SOLDI,
    fonti: ['fatture', 'costi'],
    taglie: ['M', 'L', 'XL'],
    tagliaPredefinita: 'XL',
    massimo: 1,
    apri: { etichetta: 'Analisi', percorso: '/analisi-aziendali', ruoli: SOLDI },
    impostazioniPredefinite: { mesi: 12 },
    leggiImpostazioni: (g) => ({ mesi: sceltaTra(oggetto(g).mesi, [6, 12] as const, 12) }),
  },
  'ricavi-ricorrenti': {
    tipo: 'ricavi-ricorrenti',
    titolo: 'Ricavi ricorrenti',
    descrizione: "Quanto rendono al mese e all'anno gli abbonamenti attivi.",
    gruppo: 'soldi',
    ruoli: SOLDI,
    fonti: ['abbonamenti'],
    taglie: ['S', 'M'],
    tagliaPredefinita: 'S',
    massimo: 1,
    apri: { etichetta: 'Abbonamenti', percorso: '/subscriptions', ruoli: SOLDI },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'nuovi-lead': {
    tipo: 'nuovi-lead',
    titolo: 'Nuovi lead',
    descrizione: 'Quanti contatti sono arrivati questa settimana o questo mese.',
    gruppo: 'commerciale',
    ruoli: COMMERCIALE,
    fonti: ['lead'],
    taglie: ['S', 'M'],
    tagliaPredefinita: 'S',
    massimo: 1,
    apri: { etichetta: 'Statistiche', percorso: '/leads/stats', ruoli: COMMERCIALE },
    impostazioniPredefinite: { periodo: 'mese' },
    leggiImpostazioni: (g) => ({ periodo: sceltaTra(oggetto(g).periodo, ['settimana', 'mese'] as const, 'mese') }),
  },
  'pipeline-lead': {
    tipo: 'pipeline-lead',
    titolo: 'Lead per fase',
    descrizione: 'Quanti lead ci sono in ogni fase della trattativa.',
    gruppo: 'commerciale',
    ruoli: COMMERCIALE,
    fonti: ['lead'],
    taglie: ['M', 'L'],
    tagliaPredefinita: 'M',
    massimo: 1,
    apri: { etichetta: 'Lead', percorso: '/leads', ruoli: COMMERCIALE },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'lead-per-fonte': {
    tipo: 'lead-per-fonte',
    titolo: 'Da dove arrivano i lead',
    descrizione: 'Meta, Google, sito, telefono: quanti lead da ciascuna provenienza.',
    gruppo: 'commerciale',
    ruoli: COMMERCIALE,
    fonti: ['lead'],
    taglie: ['M', 'L'],
    tagliaPredefinita: 'M',
    massimo: 1,
    apri: { etichetta: 'Statistiche', percorso: '/leads/stats', ruoli: COMMERCIALE },
    impostazioniPredefinite: { periodo: 'sempre' },
    leggiImpostazioni: (g) => ({
      periodo: sceltaTra(oggetto(g).periodo, ['mese', 'anno', 'sempre'] as const, 'sempre'),
    }),
  },
  'offerte-aperte': {
    tipo: 'offerte-aperte',
    titolo: 'Offerte in corso',
    // Niente valore in euro: le offerte hanno i prezzi ma non le quantita'.
    descrizione: 'Le offerte che aspettano una risposta, e quelle accettate da fatturare.',
    gruppo: 'commerciale',
    ruoli: COMMERCIALE,
    fonti: ['offerte'],
    taglie: ['S', 'M', 'L'],
    tagliaPredefinita: 'S',
    massimo: 1,
    apri: { etichetta: 'Offerte', percorso: '/price-lists', ruoli: COMMERCIALE },
    impostazioniPredefinite: { soloMie: false },
    leggiImpostazioni: (g) => ({ soloMie: booleano(oggetto(g).soloMie, false) }),
  },
  'ultima-campagna': {
    tipo: 'ultima-campagna',
    titolo: 'Ultima campagna',
    descrizione: "Com'è andata l'ultima campagna email: inviate, aperte, cliccate.",
    gruppo: 'strumenti',
    ruoli: ['admin', 'commerciale'],
    fonti: ['campagne'],
    taglie: ['S', 'M'],
    tagliaPredefinita: 'M',
    massimo: 1,
    apri: { etichetta: 'Campagne', percorso: '/mailing/campagne', ruoli: ['admin', 'commerciale'] },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'contatti-mailing': {
    tipo: 'contatti-mailing',
    titolo: 'Contatti mailing',
    descrizione: "Quanti contatti ricevono le email, i nuovi del mese e chi si è disiscritto.",
    gruppo: 'strumenti',
    ruoli: COMMERCIALE,
    fonti: ['contatti'],
    taglie: ['S', 'M'],
    tagliaPredefinita: 'S',
    massimo: 1,
    apri: { etichetta: 'Contatti', percorso: '/mailing/contatti', ruoli: COMMERCIALE },
    impostazioniPredefinite: {},
    leggiImpostazioni: nessunaImpostazione,
  },
  'azioni-rapide': {
    tipo: 'azioni-rapide',
    titolo: 'Azioni rapide',
    descrizione: "I collegamenti alle sezioni che usi di più; scegli tu quali.",
    gruppo: 'strumenti',
    ruoli: TUTTI,
    fonti: [],
    taglie: ['S', 'M', 'L', 'XL'],
    tagliaPredefinita: 'M',
    massimo: 1,
    impostazioniPredefinite: { collegamenti: [] },
    leggiImpostazioni: (g) => {
      const lista = oggetto(g).collegamenti;
      const ripulita = Array.isArray(lista)
        ? lista.filter((x): x is string => typeof x === 'string' && x.length <= 40)
        : [];
      return { collegamenti: [...new Set(ripulita)] };
    },
  },
  nota: {
    tipo: 'nota',
    titolo: 'Nota personale',
    descrizione: 'Un appunto tuo, visibile solo a te.',
    gruppo: 'strumenti',
    ruoli: TUTTI,
    fonti: [],
    taglie: ['S', 'M', 'L'],
    tagliaPredefinita: 'M',
    massimo: 4,
    impostazioniPredefinite: { titolo: '', testo: '' },
    leggiImpostazioni: (g) => ({
      titolo: testoLimitato(oggetto(g).titolo, MAX_TITOLO_NOTA),
      testo: testoLimitato(oggetto(g).testo, MAX_TESTO_NOTA),
    }),
  },
};

export function eTipoWidget(valore: unknown): valore is TipoWidget {
  return typeof valore === 'string' && (TIPI_WIDGET as readonly string[]).includes(valore);
}

/** Vero se il ruolo puo' vedere il widget. Senza ruolo non si vede niente. */
export function ruoloVede(tipo: TipoWidget, ruolo: UserRole | null | undefined): boolean {
  return !!ruolo && CATALOGO[tipo].ruoli.includes(ruolo);
}

/** I widget che il ruolo puo' aggiungere, nell'ordine del catalogo. */
export function widgetDisponibili(ruolo: UserRole | null | undefined): TipoWidget[] {
  return TIPI_WIDGET.filter((tipo) => ruoloVede(tipo, ruolo));
}
